import {
    CalculateAcademyResultsRequest,
    convertResultsReportOptionsToURLSearchParams,
    ResultsReportOptions,
} from "../Api/competitorResultsApi";
import { encodeBackUrl } from "../Common/currentPageBackUrl";

export default {
    languageUrls() {
        return {
            changeTo(code: string) {
                return `/language/changeTo/${code}`;
            },
        };
    },
    userPagesUrls() {
        return {
            loginPage(backUrl = ""): string {
                const path = `/${window.panelLocale}/login`;

                return backUrl ? `${path}?backUrl=${encodeBackUrl(backUrl)}` : path;
            },
            logout() {
                return "/logout";
            },
            myCompetitorsPage() {
                return `/${window.panelLocale}/my-competitors`;
            },
            userProfilePage() {
                return `/${window.panelLocale}/profile`;
            },
            manageAcademy(userId, academyId) {
                return `/${window.panelLocale}/users/${userId}/academies/${academyId}`;
            },
        };
    },
    publicPages() {
        return {
            academyPage(academyId) {
                return `/${window.panelLocale}/academies/${academyId}`;
            },
            pricingPage() {
                return `/${window.panelLocale}/pricing`;
            },
        };
    },
    userEventPagesUrls() {
        return {
            listing() {
                return `/${window.panelLocale}/users/events`;
            },
            singleEventRegistrationPage(eventId) {
                return `/${window.panelLocale}/users/events/${eventId}`;
            },
            paymentDocumentDownload(eventId, paymentDocumentId, forceDownload = true) {
                return `/api/events/${eventId}/payment-documents/${paymentDocumentId}?forceDownload=${forceDownload ? "1" : "0"}`;
            },
        };
    },
    publicEventPages(eventId, urlSlug) {
        return {
            mainEventPageUrl() {
                return `/${window.panelLocale}/events/${eventId}-${urlSlug}`;
            },
            startingListsUrl() {
                return `/${window.panelLocale}/events/${eventId}-${urlSlug}/starting-lists`;
            },
            bracketsListingUrl() {
                return `/${window.panelLocale}/events/${eventId}-${urlSlug}/brackets`;
            },
            schedulesUrl() {
                return `/${window.panelLocale}/events/${eventId}-${urlSlug}/schedules`;
            },
            scheduleUrl(parameterId: number) {
                return `/${window.panelLocale}/events/${eventId}-${urlSlug}/schedules?parameterId=${parameterId}`;
            },
            bracketUrl(parameterId, backUrl = "") {
                if (!!backUrl) {
                    const encodedBackUrl = encodeURIComponent(backUrl);
                    return `/${window.panelLocale}/events/${eventId}-${urlSlug}/brackets/${parameterId}?backUrl=${encodedBackUrl}`;
                }

                return `/${window.panelLocale}/events/${eventId}-${urlSlug}/brackets/${parameterId}`;
            },
            currentMatchesUrl() {
                return `/${window.panelLocale}/events/${eventId}-${urlSlug}/current-matches`;
            },
            resultsUrl() {
                return `/${window.panelLocale}/events/${eventId}-${urlSlug}/results`;
            },
            liveStreamsUrl() {
                return `/${window.panelLocale}/events/${eventId}-${urlSlug}/live-streams`;
            },
        };
    },
    eventRelated() {
        return {
            eventMapUrl(latitude, longitude, eventstart, eventend) {
                return `https://www.stay22.com/embed/gm?aid=martialmatch&lat=${latitude}&lng=${longitude}&eventstart=${eventstart}&eventend=${eventend}`;
            },
        };
    },
    eventAdmin() {
        return {
            manageEventsPage() {
                return `/${window.panelLocale}/manage-events`;
            },
            eventSummaryForAdmin(eventId: number): string {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/summary`;
            },
            exportStartingListsAsCsv(eventId: number): string {
                return `/api/admin/events/${eventId}/starting-lists/actions/export-csv`;
            },
            exportEventRankingsAsCsv(eventId: number): string {
                return `/api/admin/events/${eventId}/rankings/actions/export-csv`;
            },
            exportSingleEventRankingAsCsv(eventId: number, rankingId: number): string {
                return `/api/admin/events/${eventId}/rankings/${rankingId}/actions/export-csv`;
            },
            startFirstFightPage(eventId, matId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/fights/${matId}/actions/prepare`;
            },
            startIndividualFightPage(eventId: number, fightId: number): string {
                return `/${window.panelLocale}/events/${eventId}/scoreboard/${fightId}`;
            },
            categoriesManagementPage(eventId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/categories-management`;
            },
            categoriesSummaryCsv(eventId) {
                return `/api/events/${eventId}/categories-summary/csv`;
            },
            coachPassesCsv(eventId: number, queryString: string = ""): string {
                return `/api/admin/events/${eventId}/coach-passes/csv${queryString}`;
            },
            coachPassesPdf(eventId: number, queryString: string = ""): string {
                return `/api/admin/events/${eventId}/coach-passes/pdf${queryString}`;
            },
            editEventPageWithContactInfo(eventId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/edit-event?action=contactInfo`;
            },
            exportStartingListsToCsv(eventId: number): string {
                return `/api/admin/events/${eventId}/starting-lists/actions/export-csv`;
            },
            exportPaymentsToCsv(eventId: number): string {
                return `/api/admin/events/${eventId}/payments/actions/export-csv`;
            },
            liveScoreboardForMatPage(eventId, matId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/live-scoreboards/${matId}`;
            },
            bracketsListingPage(eventId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/brackets-listing`;
            },
            disciplinePackagesPage(eventId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/discipline-packages`;
            },
            createBracketPage(eventId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/create-bracket`;
            },
            showBracketPage(eventId, parameterId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/brackets/${parameterId}`;
            },
            downloadBracketPage(eventId, parameterId) {
                return `/api/admin/events/${eventId}/brackets/${parameterId}/csv`;
            },
            downloadAcademyResultsAsCsv(eventId: number, request: CalculateAcademyResultsRequest): string {
                const data = JSON.stringify(request, null, 0);

                return `/api/events/${eventId}/calculate-academy-results/csv?data=${encodeURIComponent(data)}`;
            },
            competitorResultsPage(eventId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/competitor-results`;
            },
            competitorResultsCsv(eventId: number, options: ResultsReportOptions) {
                const queryParams = convertResultsReportOptionsToURLSearchParams(options);

                return `/api/events/${eventId}/results/internal/csv?${queryParams.toString()}`;
            },
            verificationPage(eventId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/verification`;
            },
            refereeFightsPage(eventId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/start-fights`;
            },
            announcerBoardPage(eventId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/announcer-board`;
            },
            activeScheduleDelayPage(eventId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/schedule-status`;
            },
            registeredCompetitorsForUserPage(eventId, userId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/competitors?userId=${userId}`;
            },
            registeredCompetitorsForCategoryPage(eventId, parameterId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/competitors?parameterId=${parameterId}`;
            },
            specificUserOnListingPage(eventId, userId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/users?userId=${userId}`;
            },
            scheduleManagementPage(eventId, scheduleId) {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/schedules/${scheduleId}`;
            },
            createEventWizardPage(federationId: string): string {
                return `/${window.panelLocale}/pages/admin/federations/${federationId}/create-event-wizard`;
            },
            editEventPage(eventId: number): string {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/edit-event`;
            },
            editEventPageWithRegistrationStages(eventId: number): string {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/edit-event?tab=registrationStages`;
            },
            editEventPageToPublish(eventId: number): string {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/edit-event?action=publish`;
            },
            editEventPageToVerifyOrganization(eventId: number): string {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/edit-event?action=verifyOrganization`;
            },
            editEventPageToOnboardingTour(eventId: number): string {
                return `/${window.panelLocale}/pages/admin/events/${eventId}/edit-event?action=onboarding`;
            },
        };
    },
    helpCenter() {
        // Helper to get locale for help center (only pl and en supported, fallback to en)
        const getHelpCenterLocale = () => {
            const locale = window.panelLocale;
            return locale === "pl" ? "pl" : "en";
        };

        return {
            creatingNewEvent() {
                return `/${getHelpCenterLocale()}/help-center/creating-new-event`;
            },
            categoriesManagement() {
                return `/${getHelpCenterLocale()}/help-center/categories-management`;
            },
            categoriesRestrictions() {
                return `/${window.panelLocale}/help-center/categories-restrictions`;
            },
            wrestlingClassification() {
                return `/${window.panelLocale}/help-center/classification-in-wrestling-events`;
            },
            howToPlanSchedule() {
                return `/${window.panelLocale}/help-center/how-to-plan-schedule#article`;
            },
            choosingAcademy() {
                return `/${window.panelLocale}/help-center/choosing-academy#article`;
            },
            sharingSchedule() {
                return `/${window.panelLocale}/help-center/sharing-schedule#article`;
            },
            sharingSchedulePlannedVersion() {
                const locale = getHelpCenterLocale();
                const anchor = locale === "pl" ? "ustawianie-planowanej-wersji-harmonogramu" : "setting-planned-version-of-schedule";

                return `/${locale}/help-center/sharing-schedule#${anchor}`;
            },
            howToStartLiveStreaming() {
                return `/${window.panelLocale}/help-center/how-to-start-live-streaming#article`;
            },
            howDoesBracketSeedingWork() {
                return `/${window.panelLocale}/help-center/how-does-bracket-seeding-work#article`;
            },
            staffRolesAndBundles() {
                return `/${getHelpCenterLocale()}/help-center/staff-roles-and-bundles#article`;
            },
        };
    },
    superAdminUrls() {
        return {
            mainPage() {
                return `/pages/superadmin`;
            },
            manageSupportReactions(eventId) {
                return `/${window.panelLocale}/superadmin/support-reactions?eventId=${eventId}`;
            },
            manageFederation(federationId: string, federationName: string) {
                const params = new URLSearchParams({ federationId, q: federationName });
                return `/${window.panelLocale}/pages/superadmin/federations?${params.toString()}`;
            },
        };
    },
    notificationCenter() {
        return {
            createNotificationPage() {
                return `/${window.panelLocale}/notifications-center/create`;
            },
            notificationListingPage() {
                return `/${window.panelLocale}/notifications-center`;
            },
        };
    },
    federations() {
        return {
            listing() {
                return `/${window.panelLocale}/federations`;
            },
            manageFederation(federationId: string) {
                return `/${window.panelLocale}/federations/${federationId}`;
            },
            federationMembersPage(federationId: string) {
                return `/${window.panelLocale}/federations/${federationId}?section=federationMembers`;
            },
            manageEventsWithFederation(federationId: string) {
                return `/${window.panelLocale}/manage-events?federationId=${federationId}`;
            },
        };
    },
    freeScoreboard() {
        return {
            playUrl(type: "adcc" | "bjj" | "judo") {
                if (type === "bjj") {
                    return `/${window.panelLocale}/tools/free-bjj-scoreboard/play`;
                }
                if (type === "judo") {
                    return `/${window.panelLocale}/tools/free-judo-scoreboard/play`;
                }
                return `/${window.panelLocale}/tools/free-scoreboard/play`;
            },
        };
    },
};
